/**
 * LayoutRenderer — Phase 5 spike + Phase 5D polish.
 *
 * Materialises a LayoutPlan by walking `blocks[]` and dispatching each
 * `primitiveName` to the matching React component. Render-side defence
 * in depth:
 *   - unknown primitiveName → skip silently (engine should have caught,
 *     but a stale plan from a deploy boundary can sneak through)
 *   - any thrown error inside a primitive → BlockErrorBoundary catches,
 *     logs to console, and skips just that block (page stays alive)
 *
 * Phase 5D additions:
 *   - Visual dividers between blocks (thin border-top on all but the
 *     first block + breathing-room padding). Operators couldn't tell
 *     where one block ended and the next began.
 *   - Per-block info icon (top-right of each block) that toggles a small
 *     popover showing the block's rationale + primitive name. Hidden on
 *     desktop until hover; always visible on mobile. ESC + outside-click
 *     dismiss.
 *
 * SPIKE NOTES: zero behaviour for the render layer that powers the
 * actual /match/:eventId page yet. This is the dispatcher you'd
 * mount once an operator pins an LLM plan for a market. Storybook
 * stories under __spike__/ exercise both fixture mode and a live
 * fetch against /internal/layout/spike.
 */

import {
  Component,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ComponentType,
  type ErrorInfo,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import type {
  LayoutPlan,
  LayoutBlock,
  LayoutBlockStat,
  LayoutGroup,
  LayoutEntry,
} from '@aamp/shared-types';
import { useTranslation } from 'react-i18next';

/** Inlined LayoutGroup discriminator. We don't import `isLayoutGroup` from
 *  @aamp/shared-types because the pnpm + Docker shared-types cache trap
 *  (see CLAUDE.md "Locked decisions") means runtime helpers can ship
 *  stale snapshots. Type-only imports are safe; runtime helpers get
 *  inlined here per the established pattern. */
function isLayoutGroup(entry: LayoutEntry): entry is LayoutGroup {
  return (entry as LayoutGroup).kind === 'group';
}

import {
  CircularGauge,
  CountryFlag,
  EventHistoryStrip,
  GameResultCard,
  HorizontalBarComparison,
  LegendRow,
  MediaHero,
  MiniStat,
  MiniTrendChart,
  PlayerHeroRow,
  PlayerRow,
  RankChip,
  ScorePill,
  SegmentedBar,
  StatComparisonRow,
  TeamCrest,
  TeamIdentity,
  ValueBox,
} from '../primitives/index.js';

// ────────────────────────────────────────────────────────────────────────────
// Primitive registry — name (manifest) → React component.
// Keys MUST match `primitives-manifest.json` exactly (kebab-case).
// ────────────────────────────────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const PRIMITIVE_REGISTRY: Record<string, ComponentType<any>> = {
  'team-crest': TeamCrest,
  'team-identity': TeamIdentity,
  'score-pill': ScorePill,
  'legend-row': LegendRow,
  'circular-gauge': CircularGauge,
  'mini-stat': MiniStat,
  'country-flag': CountryFlag,
  'stat-comparison-row': StatComparisonRow,
  'horizontal-bar-comparison': HorizontalBarComparison,
  'segmented-bar': SegmentedBar,
  'value-box': ValueBox,
  'mini-trend-chart': MiniTrendChart,
  'event-history-strip': EventHistoryStrip,
  'player-hero-row': PlayerHeroRow,
  'player-row': PlayerRow,
  'game-result-card': GameResultCard,
  'rank-chip': RankChip,
  'media-hero': MediaHero,
};

// ────────────────────────────────────────────────────────────────────────────
// Suppression list — primitives that should NOT show an info-icon.
// Team identity + crest are self-explanatory chrome; the (i) adds noise
// and a "what does this mean?" affordance for something that means
// exactly what it shows. The popover would just say "Klublogo." which is
// already obvious.
// ────────────────────────────────────────────────────────────────────────────
const PRIMITIVES_WITHOUT_INFO_ICON = new Set<string>([
  'team-identity',
  'team-crest',
]);

// ────────────────────────────────────────────────────────────────────────────
// Per-block ErrorBoundary
// ────────────────────────────────────────────────────────────────────────────

interface BoundaryProps {
  children: ReactNode;
  primitiveName: string;
}

interface BoundaryState {
  error: Error | null;
}

class BlockErrorBoundary extends Component<BoundaryProps, BoundaryState> {
  public override state: BoundaryState = { error: null };

  public static getDerivedStateFromError(error: Error): BoundaryState {
    return { error };
  }

  public override componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.warn(
      `[LayoutRenderer] block '${this.props.primitiveName}' crashed: ${error.message}`,
      info.componentStack,
    );
  }

  public override render(): ReactNode {
    if (this.state.error) return null;
    return this.props.children;
  }
}

// ────────────────────────────────────────────────────────────────────────────
// Info icon + popover — Phase 5D
//
// Why inline state (not a context / global popover registry): each block
// has at most one popover at a time, scope is local. A useState toggle
// per block keeps the implementation honest at one screenful of code.
// The container uses CSS hover for desktop, default-visible on mobile
// (max-width: 600px in the module-less style block at the bottom).
// ────────────────────────────────────────────────────────────────────────────

/** Pretty-print "event-history-strip" → "Event history strip". */
function formatPrimitiveName(name: string): string {
  const lower = name.replace(/-/g, ' ');
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

// Per-primitive readability hints now live in the i18n catalog under
// `primitiveHelp.<primitive-name>` (cms.ui_strings), resolved at render time
// via `t()`. The LLM-emitted `caption` still wins; the catalog value is the
// localized fallback. See docs/I18N-ARCHITECTURE.md.

interface BlockInfoButtonProps {
  primitiveName: string;
  // Allow undefined explicitly for tsconfig's exactOptionalPropertyTypes.
  rationale?: string | undefined;
  /** LLM-emitted user-facing caption. When present we surface it in the
   *  popover under the "how to read it" row; when absent we fall back to
   *  the localized `primitiveHelp.<name>` catalog entry. */
  caption?: string | undefined;
  /** Stat Row v2 (UX-AUDIT-2026-07-31 R4): stat-bearing rows mount the
   *  (i) INLINE at the end of the centred label instead of the absolute
   *  top-right corner. Same button, same popover — only placement. */
  inline?: boolean;
  index: number;
}

function BlockInfoButton({
  primitiveName,
  rationale,
  caption,
  inline = false,
  index,
}: BlockInfoButtonProps): ReactElement {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // ESC dismisses; outside-click dismisses. Listeners attached only when
  // open so we don't pay an event-listener tax on every block.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onClick = (e: MouseEvent): void => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return (): void => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [open]);

  const toggle = useCallback(
    (e: ReactMouseEvent | ReactKeyboardEvent): void => {
      e.stopPropagation();
      setOpen((v) => !v);
    },
    [],
  );

  const popoverId = `aamp-block-info-${index}`;

  return (
    <div
      ref={rootRef}
      className={inline ? 'aamp-block-info aamp-block-info-inline' : 'aamp-block-info'}
    >
      <button
        type="button"
        className="aamp-block-info-btn"
        aria-label={`Show details for block ${index + 1}: ${formatPrimitiveName(primitiveName)}`}
        aria-expanded={open}
        aria-controls={popoverId}
        onClick={toggle}
      >
        i
      </button>
      {open ? (
        <div
          id={popoverId}
          role="dialog"
          aria-label={`Block details: ${formatPrimitiveName(primitiveName)}`}
          className="aamp-block-info-popover"
        >
          <div className="aamp-block-info-row">
            <div className="aamp-block-info-key">{t('layout.infoWhatShows')}</div>
            <div className="aamp-block-info-val">
              {rationale && rationale.length > 0
                ? rationale
                : t('layout.noRationale')}
            </div>
          </div>
          {/* Punter-appropriate copy ONLY (UX-AUDIT-2026-07-31 defect 11):
              the operator debug rows (primitive-name code chip, block
              index, prop keys) are gone from this surface. */}
          <div className="aamp-block-info-row">
            <div className="aamp-block-info-key">{t('layout.infoHowToRead')}</div>
            <div className="aamp-block-info-val">
              {caption && caption.length > 0
                ? caption
                : t(`primitiveHelp.${primitiveName}`, {
                    defaultValue: t('layout.defaultPrimitiveCaption'),
                  })}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Style block — kept inline so the renderer file is self-contained.
// Matches the inline-style convention already used elsewhere here (no
// CSS Module dependency for the spike surface).
// ────────────────────────────────────────────────────────────────────────────

const RENDERER_STYLES = `
.aamp-layout-block {
  position: relative;
  /* Compact-by-default law (Johan, 2026-07-31): screen space is only
     spent where a design rule demands it. Right gutter reserves the
     info-icon column so right-aligned values never sit under the (i)
     (UX-AUDIT-2026-07-31 defect 2). */
  padding: 6px 20px 6px 0;
}
/* Stat Row v2 blocks carry their (i) INLINE in the centred label, so the
   right gutter would only inset the away value off its edge rail — drop
   it. Non-stat blocks keep the absolute icon + gutter above. */
.aamp-layout-block-stat {
  padding-right: 0;
}
.aamp-layout-block + .aamp-layout-block {
  border-top: 1px solid var(--border-subtle, #e0e5eb);
}
.aamp-block-info {
  position: absolute;
  top: 6px;
  right: 4px;
  z-index: 5;
}
.aamp-block-info-btn {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 1px solid var(--border, #e2e8f0);
  background: var(--bg-surface, #ffffff);
  color: var(--text-secondary, #4d4d4d);
  font-family: 'Inter', system-ui, sans-serif;
  font-size: 11px;
  font-weight: 700;
  font-style: italic;
  line-height: 1;
  padding: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 120ms ease, background-color 120ms ease;
}
.aamp-layout-block:hover > .aamp-block-info > .aamp-block-info-btn,
.aamp-layout-block:focus-within > .aamp-block-info > .aamp-block-info-btn,
.aamp-layout-block:hover .aamp-block-info-inline > .aamp-block-info-btn,
.aamp-layout-block:focus-within .aamp-block-info-inline > .aamp-block-info-btn,
.aamp-block-info-btn[aria-expanded="true"] {
  opacity: 1;
}
/* Inline variant (Stat Row v2): the (i) sits at the end of the centred
   label — in flow, not absolute — as a smaller glyph. Same popover,
   anchored under the glyph. */
.aamp-block-info-inline {
  position: relative;
  display: inline-flex;
  align-items: center;
  flex: none;
}
.aamp-block-info-inline > .aamp-block-info-btn {
  width: 14px;
  height: 14px;
  font-size: 9px;
}
.aamp-block-info-inline > .aamp-block-info-popover {
  top: 18px;
  right: auto;
  left: 50%;
  transform: translateX(-50%);
  text-transform: none;
  letter-spacing: normal;
}
.aamp-block-info-btn:hover,
.aamp-block-info-btn:focus-visible {
  background: var(--bg-surface-subtle, #f5f6fa);
  color: var(--text-primary, #1c1c21);
  outline: none;
}
/* Mobile + narrow viewports — always visible, the hover-affordance
   doesn't exist on touch. */
@media (max-width: 600px), (hover: none) {
  .aamp-block-info-btn { opacity: 0.7; }
}
.aamp-block-info-popover {
  position: absolute;
  top: 22px;
  right: 0;
  width: 220px;
  max-width: 80vw;
  padding: 10px 12px;
  background: var(--bg-surface, #ffffff);
  border: 1px solid var(--border, #e2e8f0);
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
  font-family: 'Inter', system-ui, sans-serif;
  font-size: 12px;
  line-height: 1.45;
  color: var(--text-primary, #1c1c21);
  z-index: 20;
}
.aamp-block-info-row + .aamp-block-info-row {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid var(--border-subtle, #e0e5eb);
}
.aamp-block-info-key {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--text-muted, #80808c);
  margin-bottom: 2px;
}
.aamp-block-info-val {
  color: var(--text-primary, #1c1c21);
}
/* Stat-group kicker (UX-AUDIT-2026-07-31 R5 "group diet"): when every
   inner block of a group is a stat row, the card chrome is dropped and
   the title renders as ONE small left-aligned section kicker. Case and
   tracking follow the statrow voice dials. */
.aamp-stat-kicker {
  font-family: 'Inter', system-ui, sans-serif;
  font-size: var(--statrow-label-size, 10px);
  font-weight: 700;
  letter-spacing: var(--label-tracking, 0.08em);
  text-transform: var(--statrow-label-transform, uppercase);
  color: var(--text-muted, #80808c);
  text-align: left;
  padding: 8px 0 2px;
}
/* Phase 5D-p4 + #458 (2026-05-23) — user-facing caption rendered
   below each primitive. Distinct from operator-facing rationale
   (in the info popover). Captions are short readability hints.
   Dropped italic (it broke the column rhythm as a full-width row);
   captions now read as a calm centered subtitle that sits with the
   row above instead of pulling the eye away from the column axis. */
.aamp-block-caption {
  margin-top: 2px;
  font-family: 'Inter', system-ui, sans-serif;
  font-size: 11px;
  line-height: 1.4;
  color: var(--text-muted, #80808c);
  text-align: center;
}
`;

// ────────────────────────────────────────────────────────────────────────────
// Renderer
// ────────────────────────────────────────────────────────────────────────────

export interface LayoutRendererProps {
  plan: LayoutPlan;
  /** Container max-width — defaults to 390 to match the mobile-first
   *  primitive composition rules. Storybook stories override. */
  maxWidth?: number;
}

export function LayoutRenderer({ plan, maxWidth = 390 }: LayoutRendererProps): ReactNode {
  // ── Match-header detection ──────────────────────────────────────────────
  //
  // The LLM is asked for chrome-first, but it emits each team as its own
  // top-level block (`team-identity` × 2). Rendered naively that stacks
  // GAIS on top of Kalmar FF instead of putting them side-by-side as a
  // match header.
  //
  // Detect the pair at the top of the plan: entries[0] and entries[1] are
  // both LayoutBlocks (not groups) AND both `team-identity` / `team-crest`.
  // When true, render them inside a single horizontal flex row and skip
  // them in the normal walk.
  //
  // Phase 5E (2026-05-21): the type widened from `LayoutBlock[]` to
  // `LayoutEntry[]` (blocks-or-groups). Match-header only fires for
  // TOP-LEVEL LayoutBlocks — a group at index 0 disables the auto-header,
  // because the operator's choice to wrap the identities in a card means
  // they already want explicit framing.
  const entries = plan.blocks;
  const first = entries[0];
  const second = entries[1];
  const hasMatchHeader =
    entries.length >= 2 &&
    !!first && !isLayoutGroup(first) && isTeamHeaderPrimitive(first.primitiveName) &&
    !!second && !isLayoutGroup(second) && isTeamHeaderPrimitive(second.primitiveName);
  const walkStartIndex = hasMatchHeader ? 2 : 0;

  return (
    <>
      {/* Inline <style> keeps the renderer drop-in for any surface
          (admin iframe, Storybook story, future /match page) without a
          CSS Modules build step. The :root scope is intentional — the
          info-icon CSS is global once mounted. */}
      <style>{RENDERER_STYLES}</style>
      {/* `hk-revealed` force-reveals any `.stage-row` descendant immediately.
          Those rows (e.g. stat-comparison-row with animateOnInView) start at
          opacity:0 and rely on a `.stage-shell.visible` / `.hk-revealed`
          ancestor — which IhsCard provides but the LayoutRenderer does NOT.
          Inside an expanded market accordion the IntersectionObserver-style
          reveal never fires, leaving the rows permanently invisible (blank
          rows with only the info icon). The renderer must never hide its own
          content, so we mark the container revealed. The entrance animation
          is an IhsCard nicety, not a requirement here. */}
      <div
        className="hk-revealed"
        style={{
          width: '100%',
          maxWidth,
          boxSizing: 'border-box',
          overflowX: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {hasMatchHeader ? (
          <MatchHeaderRow
            home={first}
            away={second}
          />
        ) : null}

        {(() => {
          // Phase 5E walk. Iterate top-level entries from walkStartIndex.
          // Split into "segments" of contiguous LayoutBlocks separated by
          // LayoutGroups. Apply pairing + centering WITHIN segments.
          // Render groups as <LayoutGroupRenderer/> between segments.
          const out: ReactNode[] = [];
          let segment: { block: LayoutBlock; index: number }[] = [];
          const flushSegment = (): void => {
            if (segment.length === 0) return;
            out.push(...renderBlockSegment(segment));
            segment = [];
          };
          for (let i = walkStartIndex; i < entries.length; i++) {
            const entry = entries[i];
            if (!entry) continue;
            if (isLayoutGroup(entry)) {
              flushSegment();
              out.push(<LayoutGroupRenderer key={`g-${i}`} group={entry} index={i} />);
            } else {
              segment.push({ block: entry, index: i });
            }
          }
          flushSegment();
          return out;
        })()}
      </div>
    </>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Match-header row — renders two team-identity (or team-crest) blocks as
// a single horizontal row instead of two stacked entries.
//
// Layout: [home identity] · "VS" divider · [away identity]
// Both sides flex-1 so the row stays balanced regardless of name length.
// No info-icon (the suppression list also excludes these primitives).
// ────────────────────────────────────────────────────────────────────────────

function isTeamHeaderPrimitive(name: string | undefined): boolean {
  return name === 'team-identity' || name === 'team-crest';
}

interface MatchHeaderRowProps {
  home: LayoutBlock;
  away: LayoutBlock;
}

function MatchHeaderRow({ home, away }: MatchHeaderRowProps): ReactElement | null {
  const HomeComp = PRIMITIVE_REGISTRY[home.primitiveName];
  const AwayComp = PRIMITIVE_REGISTRY[away.primitiveName];
  if (!HomeComp || !AwayComp) return null;

  // Override the LLM's align prop. The match-header context is "this is
  // chrome at the top of the page", and chrome wants crests CENTRED above
  // their respective value columns in the stat-comparison-rows below.
  // The LLM's `align="left"/"right"` was an OK semantic ("which side is
  // this team on") but the visual outcome was right-justified crests
  // hugging the edges — readers expect the crest to sit over the data,
  // not in the corner. Force `align="center"` and let the flex column
  // (also centered) carry the left/right orientation.
  const homeProps = { ...home.props, align: 'center' };
  const awayProps = { ...away.props, align: 'center' };

  // Stat Row v2 relationship (UX-AUDIT-2026-07-31): the body rows pin the
  // home value to the LEFT edge and the away value to the RIGHT edge with
  // the label owning the centre — the values form vertical rails at the
  // edges, not centred columns. The header keeps its own 2fr/auto/2fr
  // grid with CENTRED identities: the home crest hovers over the left
  // value rail, the away crest over the right rail, and the "VS" cell
  // sits on the same centre axis as the row labels. The header does NOT
  // share the rows' exact column template (a crest pinned hard to the
  // edge would clip and read as misalignment); the contract is
  // side-correspondence — home LEFT, centre label axis, away RIGHT — not
  // pixel-identical tracks.
  return (
    <div
      className="aamp-layout-block aamp-match-header"
      style={{
        display: 'grid',
        // #457 (2026-05-23) — widen team columns so full names like
        // "Hammarby IF" don't truncate. Previous 1fr/2fr/1fr at 358px
        // gave each team ~83px; "Hammarby IF" needs ~100px on one
        // line. New 2fr/auto/2fr lets each team take ~150px and lets
        // the "VS" center column be only as wide as the label needs
        // (~36px). Team names wrap to 2 lines if still too narrow.
        gridTemplateColumns: 'minmax(0, 2fr) minmax(min-content, auto) minmax(0, 2fr)',
        alignItems: 'center',
        gap: 10,
        padding: '10px 0',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'center', minWidth: 0 }}>
        <BlockErrorBoundary primitiveName={home.primitiveName}>
          <HomeComp {...homeProps} />
        </BlockErrorBoundary>
      </div>
      <div
        aria-hidden
        style={{
          textAlign: 'center',
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.08em',
          color: 'var(--text-muted, #80808c)',
          textTransform: 'uppercase',
        }}
      >
        VS
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', minWidth: 0 }}>
        <BlockErrorBoundary primitiveName={away.primitiveName}>
          <AwayComp {...awayProps} />
        </BlockErrorBoundary>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Generic "pair these consecutive blocks horizontally" detector.
//
// PURELY A RENDERER CONCERN — the engine + LLM do not know this set
// exists, and they don't need to. The contract from the engine's POV is:
// "emit blocks in order; consecutive same-type blocks render side-by-
// side iff their primitiveName is in this set". The LLM still emits two
// separate blocks (one per team, home first per the orientation rule);
// the renderer collapses the pair visually at paint time.
//
// Why no engine-side mirror: pair-detection has zero impact on data
// validation, persistence, or cache keys. Moving it to shared-types
// would couple the layers without adding correctness. If a future
// design DOES need engine awareness (e.g. cost budgeting per visual
// row), promote then — not before.
//
// Listed primitives are ones whose semantic is "represents one team's
// value in a compare-to-the-other context": two rank-chips (#9 vs #16),
// two country-flags (group-stage opponents), two mini-stats (one per
// team), two value-boxes (one big number per team). Adding to this set
// is a render-side change with no engine coordination required.
// ────────────────────────────────────────────────────────────────────────────
const PAIRABLE_PRIMITIVES = new Set<string>([
  'rank-chip',
  'country-flag',
  'mini-stat',
  'value-box',
  // 2026-05-23 (Johan request): two consecutive event-history-strips
  // (form senaste 5 per team) now render side-by-side per team instead
  // of stacked. A single strip alone still falls through to the
  // CENTERED_FULL_WIDTH treatment (no pair partner → no pairing).
  'event-history-strip',
]);

// Primitives that need MORE horizontal real estate when paired than the
// default 1fr/2fr/1fr stat-row grid affords. Form strips with 5 W/D/L
// chips per team need ~140-180px each; the default 1fr column at 390px
// viewport gives ~80px — chips overflow. For these, BodyPairRow uses a
// wider, label-less grid (1fr / gap / 1fr) so each side gets ~180px.
const WIDE_PAIR_PRIMITIVES = new Set<string>([
  'event-history-strip',
]);

// ────────────────────────────────────────────────────────────────────────────
// Centered full-width primitives — these block types use the FULL row width
// (they're never paired left/right per team) but their internal content
// would otherwise hug the left edge, which visually breaks the rhythm of
// surrounding stat-comparison-rows that have centred values.
//
// Why: 5D-v6 polish (2026-05-21). With Djurgårdens form-dots row sitting
// flush-left and the value rows above/below sitting centred, the reader's
// eye reads it as "jumping". Centering the strip resolves the rhythm
// without forcing the strip into the 1fr/2fr/1fr grid (where 5 chips at
// 24-32px each would never fit in a 64px column).
//
// Listed primitives are ones whose visual is a horizontal stripe with no
// per-team home/away axis, but which should sit centred in the row to
// match the rhythm of stat-comparison-rows above/below.
// ────────────────────────────────────────────────────────────────────────────
const CENTERED_FULL_WIDTH_PRIMITIVES = new Set<string>([
  'event-history-strip',
  'mini-trend-chart',
  'segmented-bar',
  'horizontal-bar-comparison',
  // 2026-05-28 (Fase 1): a LONE value-box centres so the engine's
  // duplicate-value coercer output (stat-comparison-row home===away → one
  // neutral value-box) sits on the same central axis as the stat rows
  // around it instead of hugging the left edge. Paired value-boxes (two
  // consecutive, one per team) still pair via PAIRABLE_PRIMITIVES — the
  // pairing check runs before this single-block centring path, so grid-2
  // season-stat groups are unaffected.
  'value-box',
]);

// A binary event-history-strip (BTTS / Over 2.5 "senaste N" — Ja/Nej chips)
// carries N chips (typically 6). When two of them pair side-by-side, each
// half-width column collapses the nowrap chip row to ~36px and clips all but
// ~2 chips (operator audit 2026-05-28: "I can only see a couple chips even
// though it should be 6"). So binary strips must NOT pair — they render
// full-width stacked, where all N chips fit. Three-state FORM strips
// (V/O/F, ~5 chips) keep pairing per the 2026-05-23 decision; those are
// narrower and were explicitly requested side-by-side.
function isBinaryHistoryStrip(block: LayoutBlock): boolean {
  if (block.primitiveName !== 'event-history-strip') return false;
  const mode = (block.props as { mode?: unknown } | undefined)?.mode;
  // mode defaults to 'binary' when unset (matches engine + primitive default).
  return typeof mode !== 'string' || mode === 'binary';
}

// ────────────────────────────────────────────────────────────────────────────
// Stat Row v2 (UX-AUDIT-2026-07-31): structured `block.stat` → row props.
//
// When a block carries `stat` (the LayoutBlockStat contract in
// shared-types/layout-plan.ts), the RENDERER owns geometry: sides are
// data, home pins LEFT, away pins RIGHT, singles are attributed to their
// side, ties render as a pair. The LLM's free-text props stop steering
// layout for these blocks.
//
// Which blocks qualify:
//   - `stat-comparison-row` with `stat` → props derived from stat
//     (metricLabel, home/away or subject+value, renderType, tie).
//   - `value-box` / `mini-stat` with `stat.subject` → rendered as a
//     Stat Row v2 single-side (or tie-pair) row INSTEAD of the neutral
//     centred box. Legacy statless boxes keep today's behaviour.
//   - anything else → null (normal primitive dispatch).
// ────────────────────────────────────────────────────────────────────────────

interface StatRowV2Props {
  label: string;
  home?: string | number;
  away?: string | number;
  subject?: 'home' | 'away';
  renderType?: 'numbers' | 'bar';
  tie?: boolean;
  lead?: 'home' | 'away' | 'both';
}

/** Read a `lead` emitted in the block's free props (engine mechanical-lead
 *  post-process writes it there) — emphasis semantics are unchanged. */
function leadFromProps(props: Record<string, unknown> | undefined): StatRowV2Props['lead'] {
  const lead = props?.['lead'];
  return lead === 'home' || lead === 'away' || lead === 'both' ? lead : undefined;
}

function statRowPropsFromBlock(block: LayoutBlock): StatRowV2Props | null {
  const stat: LayoutBlockStat | undefined = block.stat;
  if (!stat) return null;
  const name = block.primitiveName;
  const isRowPrimitive = name === 'stat-comparison-row';
  const isBoxPrimitive = name === 'value-box' || name === 'mini-stat';
  if (!isRowPrimitive && !isBoxPrimitive) return null;
  // Value-box / mini-stat only convert when the stat says WHOSE value it
  // is — without attribution the legacy box rendering stands.
  if (isBoxPrimitive && stat.subject === undefined) return null;

  const base: StatRowV2Props = { label: stat.metricLabel };
  const lead = leadFromProps(block.props);
  if (lead !== undefined) base.lead = lead;
  if (stat.renderType !== undefined) base.renderType = stat.renderType;

  // subject='both': identical for both teams — ALWAYS a tie pair, never a
  // neutral orphan (the duplicate-value coercer's centred box is retired).
  if (stat.subject === 'both') {
    const v = stat.value ?? stat.home ?? stat.away;
    if (v === undefined) return null;
    return { ...base, home: v, away: v, tie: true };
  }
  // One-sided: the value renders in the subject's cell, quiet "–" opposite.
  if (stat.subject === 'home' || stat.subject === 'away') {
    const v = stat.value ?? (stat.subject === 'home' ? stat.home : stat.away);
    if (v === undefined) return null;
    return stat.subject === 'home'
      ? { ...base, home: v, subject: 'home' }
      : { ...base, away: v, subject: 'away' };
  }
  // Two-sided pair — tie when the engine sent literally equal values.
  if (stat.home !== undefined && stat.away !== undefined) {
    return { ...base, home: stat.home, away: stat.away, tie: stat.home === stat.away };
  }
  return null;
}

/** Render a block as a Stat Row v2 row: inline (i) at the end of the
 *  centred label, no right gutter (the away value owns the right edge),
 *  divider + caption behaviour unchanged.
 *
 *  `rowProps` is either the stat-derived prop set (stat-bearing blocks)
 *  or the block's own engine-validated free props (STATLESS
 *  stat-comparison-row emissions — the LLM may legally omit `stat`, but
 *  the v2 anatomy still pins the away value to the RIGHT edge, so the
 *  absolute corner (i) would sit on top of it; EVERY stat row therefore
 *  takes the inline glyph + no-gutter treatment). */
function renderStatRowBlock(
  block: LayoutBlock,
  rowProps: StatRowV2Props | LayoutBlock['props'],
  index: number,
): ReactElement {
  // Blind-dispatch typing for the statless arm — mirrors the registry's
  // `<Primitive {...block.props} />` pattern: free props pass through
  // exactly as the generic path would have sent them.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const Row: ComponentType<any> = StatComparisonRow;
  return (
    <div key={index} className="aamp-layout-block aamp-layout-block-stat">
      <BlockErrorBoundary primitiveName={block.primitiveName}>
        <Row
          {...rowProps}
          labelTrailing={
            <BlockInfoButton
              inline
              primitiveName={block.primitiveName}
              rationale={block.rationale}
              caption={block.caption}
              index={index}
            />
          }
        />
      </BlockErrorBoundary>
      {block.caption && block.caption.length > 0 ? (
        <div className="aamp-block-caption">{block.caption}</div>
      ) : null}
    </div>
  );
}

interface BodyPairRowProps {
  home: LayoutBlock;
  away: LayoutBlock;
  index: number;
}

/** Split a "Prefix: TeamName" caption into prefix + tail. Returns the
 *  shared prefix when both captions share one (e.g. both start with
 *  "Form senaste 5: "), plus the per-side tails. Used by BodyPairRow
 *  to hoist the shared header out and leave the team-specific bit on
 *  each strip. */
function stripSharedCaptionPrefix(
  a: string | undefined,
  b: string | undefined,
): { prefix: string; aTail: string; bTail: string } | null {
  if (!a || !b) return null;
  const sep = ': ';
  const aIdx = a.indexOf(sep);
  const bIdx = b.indexOf(sep);
  if (aIdx === -1 || bIdx === -1) return null;
  if (a.slice(0, aIdx) !== b.slice(0, bIdx)) return null;
  return {
    prefix: a.slice(0, aIdx),
    aTail: a.slice(aIdx + sep.length),
    bTail: b.slice(bIdx + sep.length),
  };
}

function BodyPairRow({ home, away, index }: BodyPairRowProps): ReactElement | null {
  const HomeComp = PRIMITIVE_REGISTRY[home.primitiveName];
  const AwayComp = PRIMITIVE_REGISTRY[away.primitiveName];
  if (!HomeComp || !AwayComp) return null;

  const suppressInfo = PRIMITIVES_WITHOUT_INFO_ICON.has(home.primitiveName);
  const combinedRationale =
    [home.rationale, away.rationale].filter(Boolean).join(' · ') ||
    undefined;

  // #452 (2026-05-23) — captions live at `block.props.caption` (the
  // primitive reads them from props), NOT at the block top level. The
  // legacy code in this function was reading `home.caption` which is
  // always undefined, so the shared-prefix detection never fired in
  // production. Read from props to actually catch shared prefixes.
  const homeCaption: string | undefined =
    typeof (home.props as { caption?: unknown } | undefined)?.caption === 'string'
      ? ((home.props as { caption: string }).caption)
      : undefined;
  const awayCaption: string | undefined =
    typeof (away.props as { caption?: unknown } | undefined)?.caption === 'string'
      ? ((away.props as { caption: string }).caption)
      : undefined;

  // Caption strategy:
  //  - Identical captions → keep one.
  //  - Shared "Prefix: A" / "Prefix: B" → hoist prefix as the pair
  //    caption AND DROP per-side captions entirely (#452). The widget's
  //    TeamIdentity row already labels home-left / away-right; repeating
  //    the team name under each value is noise.
  //  - Anything else → concat with " · ".
  const sharedPrefix = stripSharedCaptionPrefix(homeCaption, awayCaption);
  let combinedCaption: string | undefined;
  let homeProps = home.props;
  let awayProps = away.props;
  if (homeCaption && awayCaption && homeCaption === awayCaption) {
    combinedCaption = homeCaption;
  } else if (sharedPrefix) {
    combinedCaption = sharedPrefix.prefix;
    // Drop per-side caption — the team identity is already established
    // by the row's left/right position + the widget's TeamIdentity header.
    homeProps = { ...(home.props ?? {}), caption: undefined };
    awayProps = { ...(away.props ?? {}), caption: undefined };
  } else {
    combinedCaption = [homeCaption, awayCaption].filter(Boolean).join(' · ') || undefined;
  }

  // #458 (2026-05-23) — wide-pair primitives still force size='sm'
  // so 5 chips fit per column at ~150px (2fr column on 358px stage
  // = ~140px, sm chips = 24px × 5 + 4 × 4 gap = 136px, fits with
  // breathing room). The WIDE_PAIR distinction is now ONLY about
  // chip-size — the grid template itself is uniform across all pairs.
  if (WIDE_PAIR_PRIMITIVES.has(home.primitiveName)) {
    homeProps = { ...(homeProps ?? {}), size: 'sm' };
    awayProps = { ...(awayProps ?? {}), size: 'sm' };
  }

  // #458 (2026-05-23) — caption always renders in the CENTER LABEL
  // CELL of the unified grid, never as a hoisted header. This keeps
  // every paired row on the same column axis as stat-comparison-rows
  // and MatchHeaderRow. Form strips' "Form senaste 5" now sits in the
  // label cell between the two chip rows — same axis as everything
  // else above and below.

  return (
    <div key={index} className="aamp-layout-block">
      {suppressInfo ? null : (
        <BlockInfoButton
          primitiveName={home.primitiveName}
          rationale={combinedRationale}
          caption={combinedCaption}
          index={index}
        />
      )}
      {/* Unified pair grid (#458) — same 2fr / auto / 2fr as
       *  MatchHeaderRow + StatComparisonRow. Combined caption (if
       *  any) sits in the center label cell so the home/away axis
       *  is preserved across every row of the card. */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns:
            'minmax(0, 2fr) minmax(min-content, auto) minmax(0, 2fr)',
          alignItems: 'center',
          gap: 10,
          padding: '6px 0',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'center', minWidth: 0 }}>
          <BlockErrorBoundary primitiveName={home.primitiveName}>
            <HomeComp {...homeProps} />
          </BlockErrorBoundary>
        </div>
        <div
          style={{
            fontSize: 12,
            fontWeight: 500,
            color: '#6B6B6B',
            textAlign: 'center',
            minWidth: 0,
            maxWidth: '100%',
            overflowWrap: 'anywhere',
          }}
        >
          {combinedCaption ?? ''}
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', minWidth: 0 }}>
          <BlockErrorBoundary primitiveName={away.primitiveName}>
            <AwayComp {...awayProps} />
          </BlockErrorBoundary>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Block-segment renderer — shared between top-level walk and group-stack
// ────────────────────────────────────────────────────────────────────────────
//
// Phase 5E (2026-05-21). Walks a contiguous slice of LayoutBlocks and
// applies the existing auto-rules:
//   - Pairing: consecutive same-primitive blocks in PAIRABLE_PRIMITIVES
//     render side-by-side via BodyPairRow.
//   - Centering: single blocks in CENTERED_FULL_WIDTH_PRIMITIVES get wrapped
//     in a flex-center container so their stripe sits centred in the row.
//
// Pulled out of the main render walk so LayoutGroupRenderer (when mode
// is 'stack') can reuse the same rules inside a card. Top-level callers
// pass the entry's global index for stable React keys; group callers
// pass local indices within the group (still stable because each group
// is uniquely keyed by its own index).

interface SegmentItem {
  block: LayoutBlock;
  index: number;
}

function renderBlockSegment(items: SegmentItem[]): ReactElement[] {
  // Pre-walk to identify pair leads (consecutive same-primitive in
  // PAIRABLE_PRIMITIVES). We track LOCAL positions in `items`, not
  // global indices.
  const pairLeadAt = new Set<number>();
  const pairFollowAt = new Set<number>();
  for (let i = 0; i < items.length - 1; i++) {
    if (pairFollowAt.has(i)) continue;
    const a = items[i]!;
    const b = items[i + 1]!;
    if (
      a.block.primitiveName === b.block.primitiveName &&
      PAIRABLE_PRIMITIVES.has(a.block.primitiveName) &&
      // Binary BTTS/Over strips must not pair — half-width clips their 6
      // chips. They fall through to full-width stacked rendering instead.
      !isBinaryHistoryStrip(a.block) &&
      !isBinaryHistoryStrip(b.block) &&
      // Stat Row v2: blocks with structured `stat` render as side-
      // anchored stat rows (each row already carries its home/away
      // geometry) — never as a BodyPairRow.
      statRowPropsFromBlock(a.block) === null &&
      statRowPropsFromBlock(b.block) === null
    ) {
      // 2026-05-28 — pairing also requires a SHARED caption prefix
      // (e.g. both "Form senaste 5: …"). Without this guard, the layout
      // builder pairs a BTTS strip with a Form strip just because both
      // happen to be event-history-strip primitives. Operator audit
      // (Double Chance card): two unrelated strips ended up in the
      // 2fr/auto/2fr grid with a concatenated "BTTS senaste 6: AIK · Form
      // senaste 5: AIK" label, and each chip column was crushed to ~30px
      // so only 1 chip rendered per side. Same-primitive is necessary
      // but not sufficient — they must also describe the SAME metric on
      // home vs away (= shared caption prefix).
      const aCap =
        typeof (a.block.props as { caption?: unknown } | undefined)?.caption === 'string'
          ? ((a.block.props as { caption: string }).caption)
          : undefined;
      const bCap =
        typeof (b.block.props as { caption?: unknown } | undefined)?.caption === 'string'
          ? ((b.block.props as { caption: string }).caption)
          : undefined;
      const sharedPrefix = (() => {
        if (!aCap || !bCap) return aCap === undefined && bCap === undefined;
        if (aCap === bCap) return true;
        const sep = ': ';
        const aIdx = aCap.indexOf(sep);
        const bIdx = bCap.indexOf(sep);
        if (aIdx === -1 || bIdx === -1) return false;
        return aCap.slice(0, aIdx) === bCap.slice(0, bIdx);
      })();
      if (sharedPrefix) {
        pairLeadAt.add(i);
        pairFollowAt.add(i + 1);
      }
    }
  }

  const out: ReactElement[] = [];
  for (let i = 0; i < items.length; i++) {
    if (pairFollowAt.has(i)) continue; // rendered as pair-follow already
    const { block, index } = items[i]!;
    if (pairLeadAt.has(i)) {
      const next = items[i + 1]!;
      out.push(
        <BodyPairRow
          key={`pair-${index}`}
          home={block}
          away={next.block}
          index={index}
        />,
      );
      continue;
    }
    out.push(renderSingleBlock(block, index));
  }
  return out;
}

/** Render one LayoutBlock as a top-level block row (with info icon,
 *  divider, optional caption, optional centred-stripe wrapper). */
function renderSingleBlock(block: LayoutBlock, index: number): ReactElement {
  // Stat Row v2: structured `stat` wins over free-text props. Covers
  // stat-comparison-row blocks AND value-box / mini-stat singles with
  // attribution (rendered as a side-anchored row, never a neutral box).
  const statRow = statRowPropsFromBlock(block);
  if (statRow) return renderStatRowBlock(block, statRow, index);

  // EVERY stat-comparison-row — including legacy STATLESS emissions the
  // LLM may legally send — takes the stat-row treatment: inline (i) in
  // the centred label + no right gutter. The v2 anatomy pins the away
  // value to the RIGHT edge regardless of `stat`, so the absolute
  // corner icon would collide with it (live evidence 2026-07-31:
  // "Point (sæson)" / "Mål for (tabel)" rows). Free props pass through
  // unchanged.
  if (block.primitiveName === 'stat-comparison-row') {
    return renderStatRowBlock(block, block.props, index);
  }

  const Primitive = PRIMITIVE_REGISTRY[block.primitiveName];
  if (!Primitive) {
    // eslint-disable-next-line no-console
    console.warn(
      `[LayoutRenderer] unknown primitive '${block.primitiveName}' (block #${index}) — skipping`,
    );
    return <div key={`unknown-${index}`} className="aamp-layout-block" aria-hidden />;
  }
  const suppressInfoIcon = PRIMITIVES_WITHOUT_INFO_ICON.has(block.primitiveName);
  // Stat-bearing value-boxes never take the neutral centred treatment
  // (UX-AUDIT-2026-07-31 defect 4) — only legacy STATLESS boxes centre.
  const shouldCenter =
    CENTERED_FULL_WIDTH_PRIMITIVES.has(block.primitiveName) &&
    !(block.primitiveName === 'value-box' && block.stat !== undefined);
  const primitiveEl = (
    <BlockErrorBoundary primitiveName={block.primitiveName}>
      <Primitive {...block.props} />
    </BlockErrorBoundary>
  );
  return (
    <div key={index} className="aamp-layout-block">
      {suppressInfoIcon ? null : (
        <BlockInfoButton
          primitiveName={block.primitiveName}
          rationale={block.rationale}
          caption={block.caption}
          index={index}
        />
      )}
      {shouldCenter ? (
        <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
          {primitiveEl}
        </div>
      ) : (
        primitiveEl
      )}
      {block.caption && block.caption.length > 0 ? (
        <div className="aamp-block-caption">{block.caption}</div>
      ) : null}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// LayoutGroup renderer — labelled card containing inner blocks
// ────────────────────────────────────────────────────────────────────────────
//
// Phase 5E (2026-05-21). Renders a `LayoutGroup` as a card with optional
// title and one of four layout modes:
//   - 'stack' (default): vertical column. Inner blocks apply the same
//     pair-detection + centering rules as the top-level walk via
//     `renderBlockSegment` — operators expect identical behaviour inside
//     a card to outside one.
//   - 'grid-2' / 'grid-3' / 'grid-5': explicit CSS grid with N columns.
//     Each inner block occupies one cell, centred in its column. NO
//     pairing or centering wrapper — the explicit grid IS the layout.
//
// Why no info-icon on the group itself: titles + captions on the group
// already explain it. Per-block info icons inside the group still appear
// (useful for explaining individual gauges in a "Season coverage" grid).

interface LayoutGroupRendererProps {
  group: LayoutGroup;
  /** Top-level entry index — used for stable React keys. */
  index: number;
}

function LayoutGroupRenderer({ group, index }: LayoutGroupRendererProps): ReactElement {
  // ── Group diet (UX-AUDIT-2026-07-31 R5) ────────────────────────────────
  // When EVERY inner block renders as a Stat Row v2 row, the card chrome
  // is pure cost: unwrap to plain divider-separated stat rows with the
  // group title as one small left-aligned section kicker. Mixed or
  // statless groups keep the card below.
  const allStatRows =
    group.blocks.length > 0 &&
    group.blocks.every((b) => statRowPropsFromBlock(b) !== null);
  if (allStatRows) {
    return (
      <section className="aamp-layout-group aamp-layout-group-stat">
        {group.title ? <div className="aamp-stat-kicker">{group.title}</div> : null}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {group.blocks.map((b, j) => {
            const statRow = statRowPropsFromBlock(b);
            // `every` above guarantees non-null; guard for the type system.
            return statRow ? renderStatRowBlock(b, statRow, j) : null;
          })}
        </div>
        {group.caption ? (
          <div className="aamp-block-caption" style={{ marginTop: 4 }}>
            {group.caption}
          </div>
        ) : null}
      </section>
    );
  }

  const mode = group.layoutMode ?? 'stack';
  const gridTemplateColumns =
    mode === 'grid-2' ? 'repeat(2, minmax(0, 1fr))'
    : mode === 'grid-3' ? 'repeat(3, minmax(0, 1fr))'
    : mode === 'grid-5' ? 'repeat(5, minmax(0, 1fr))'
    : null;

  const inner: ReactElement[] = (() => {
    if (mode === 'stack') {
      // Stack mode: reuse the segment renderer for consistent pair +
      // center semantics with top-level walks.
      return renderBlockSegment(
        group.blocks.map((b, j): SegmentItem => ({ block: b, index: j })),
      );
    }
    // Grid mode: explicit columns, each block one cell. No pairing.
    return group.blocks.map((b, j): ReactElement => {
      const P = PRIMITIVE_REGISTRY[b.primitiveName];
      if (!P) {
        // eslint-disable-next-line no-console
        console.warn(
          `[LayoutRenderer] unknown primitive '${b.primitiveName}' in group #${index}/${j} — skipping`,
        );
        return <div key={`g-${index}-${j}-unknown`} aria-hidden />;
      }
      return (
        <div
          key={`g-${index}-${j}`}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 6,
            minWidth: 0,
          }}
        >
          <BlockErrorBoundary primitiveName={b.primitiveName}>
            <P {...b.props} />
          </BlockErrorBoundary>
          {b.caption ? (
            <div
              style={{
                fontSize: 11,
                color: 'var(--text-muted, #6e6e75)',
                textAlign: 'center',
                lineHeight: 1.3,
                maxWidth: '100%',
                overflowWrap: 'anywhere',
              }}
            >
              {b.caption}
            </div>
          ) : null}
        </div>
      );
    });
  })();

  return (
    <section
      className="aamp-layout-group"
      style={{
        background: 'var(--bg-surface, #ffffff)',
        border: '1px solid var(--border-subtle, #e0e5eb)',
        padding: 10,
        borderRadius: 8,
        marginTop: 8,
        marginBottom: 2,
      }}
    >
      {group.title ? (
        <div
          style={{
            fontSize: 11,
            color: 'var(--text-muted, #6e6e75)',
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            fontWeight: 700,
            marginBottom: 6,
          }}
        >
          {group.title}
        </div>
      ) : null}
      {gridTemplateColumns ? (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns,
            gap: 10,
            alignItems: 'start',
          }}
        >
          {inner}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column' }}>{inner}</div>
      )}
      {group.caption ? (
        <div className="aamp-block-caption" style={{ marginTop: 4 }}>
          {group.caption}
        </div>
      ) : null}
    </section>
  );
}
